import React, { useState } from 'react';
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, IconButton, TableSortLabel } from '@mui/material';
import { RenderParticipantName } from './Elements';
import StyledLink from '../utils/StyledLink';
import Trend from '../utils/Trend';
import InfoIcon from "@mui/icons-material/Info";
import { useParticipantRanks } from '../utils/useParticipantRanks';

interface Props {
    data: any[];
    onParticipantClick: (participantId: number, name: string) => void;
}

const FrequencyTable: React.FC<Props> = ({ data, onParticipantClick }) => {
    const [sortConfig, setSortConfig] = useState<{ key: string, direction: 'asc' | 'desc' } | null>(null);
    const { ranksByParticipantId } = useParticipantRanks();

    const sortedData = React.useMemo(() => {
        if (sortConfig !== null) {
            return [...data].sort((a, b) => {
                const aValue = a[sortConfig.key];
                const bValue = b[sortConfig.key];

                if (aValue === null || aValue === undefined) return 1;
                if (bValue === null || bValue === undefined) return -1;

                if (sortConfig.key === 'total_entries') {
                    const aNum = Number(aValue);
                    const bNum = Number(bValue);
                    return sortConfig.direction === 'asc' ? aNum - bNum : bNum - aNum;
                }

                if (aValue < bValue) {
                    return sortConfig.direction === 'asc' ? -1 : 1;
                }
                if (aValue > bValue) {
                    return sortConfig.direction === 'asc' ? 1 : -1;
                }
                return 0;
            });
        }
        return data;
    }, [data, sortConfig]);

    const requestSort = (key: string) => {
        let direction: 'asc' | 'desc' = 'asc';
        if (sortConfig && sortConfig.key === key && sortConfig.direction === 'asc') {
            direction = 'desc';
        }
        setSortConfig({ key, direction });
    };

    return (
        <TableContainer component={Paper}>
            <Table>
                <TableHead>
                    <TableRow>
                        <TableCell />
                        <TableCell>
                            <TableSortLabel
                                active={sortConfig?.key === 'participant_name'}
                                direction={sortConfig?.direction}
                                onClick={() => requestSort('participant_name')}
                            >
                                Участник
                            </TableSortLabel>
                        </TableCell>
                        <TableCell>
                            <TableSortLabel
                                active={sortConfig?.key === 'total_entries'}
                                direction={sortConfig?.direction}
                                onClick={() => requestSort('total_entries')}
                            >
                                Записей
                            </TableSortLabel>
                        </TableCell>
                        <TableCell>
                            <TableSortLabel
                                active={sortConfig?.key === 'total_contests'}
                                direction={sortConfig?.direction}
                                onClick={() => requestSort('total_contests')}
                            >
                                Разных заруб
                            </TableSortLabel>
                        </TableCell>
                        <TableCell>
                            <TableSortLabel
                                active={sortConfig?.key === 'most_frequent_contest'}
                                direction={sortConfig?.direction}
                                onClick={() => requestSort('most_frequent_contest')}
                            >
                                Наиболее частая заруба
                            </TableSortLabel>
                        </TableCell>
                        <TableCell>
                            <TableSortLabel
                                active={sortConfig?.key === 'updated_at'}
                                direction={sortConfig?.direction}
                                onClick={() => requestSort('updated_at')}
                            >
                                Последняя активность
                            </TableSortLabel>
                        </TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {sortedData.map((row) => (
                        <TableRow
                            key={row.participant_id}
                            sx={{
                                cursor: 'pointer',
                                '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' },
                            }}
                            onClick={() => onParticipantClick(row.participant_id, row.participant_name)}
                        >
                            <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
                                <IconButton
                                    onClick={() => onParticipantClick(row.participant_id, row.participant_name)}
                                    size="small"
                                    aria-label="Подробнее"
                                >
                                    <InfoIcon color="primary" />
                                </IconButton>
                            </TableCell>
                            <TableCell>
                                {RenderParticipantName(
                                  row.participant_id,
                                  row.participant_name,
                                  row.profile_picture,
                                  true,
                                  ranksByParticipantId.get(row.participant_id) ?? []
                                )}
                            </TableCell>
                            <TableCell>
                                <Trend trend={row.activity_trend} showSteady={false} /> {row.total_entries}
                            </TableCell>
                            <TableCell>
                                <Trend trend={row.versatility_trend} showSteady={false} /> {row.total_contests}
                            </TableCell>
                            <TableCell>
                                <StyledLink to={`/contest/${row.most_frequent_contest_id}`}>{row.most_frequent_contest}</StyledLink>
                            </TableCell>
                            <TableCell>{new Date(row.updated_at).toLocaleString()}</TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
    );
};

export default FrequencyTable;